import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { GAME_TYPES_TYPE, GAME_TYPES_CONFIG, OPERAND_TYPE } from '../../constants/constants';
import { renderValue, performOperation } from '../screens/GameScreen/utils';
import { isMobile } from '@mightybyte/rnw.utils.device-info';

type TOperationChartProps = {
  gameType: GAME_TYPES_TYPE | 'All';
  width?: number;
  bottomPadding?: number;
  rightPadding?: number;
  wrongAnswers: { left: OPERAND_TYPE; right: OPERAND_TYPE }[][];
  correctAnswers: { left: OPERAND_TYPE; right: OPERAND_TYPE }[][];
};

// ✅ Helper: flatten and track recency
const findMostRecentStatus = (
  lop: OPERAND_TYPE,
  rop: OPERAND_TYPE,
  gameType: GAME_TYPES_TYPE,
  correctAnswers: { left: OPERAND_TYPE; right: OPERAND_TYPE }[][],
  wrongAnswers: { left: OPERAND_TYPE; right: OPERAND_TYPE }[][],
): 'correct' | 'wrong' | 'unanswered' => {
  const lopStr = renderValue(lop, gameType);
  const ropStr = renderValue(rop, gameType);

  let latestStatus: 'correct' | 'wrong' | 'unanswered' = 'unanswered';
  let latestRoundIndex = -1;

  // check correct first
  correctAnswers.forEach((round, roundIndex) => {
    round.forEach(c => {
      if (
        renderValue(c.left, gameType) === lopStr &&
        renderValue(c.right, gameType) === ropStr &&
        roundIndex >= latestRoundIndex
      ) {
        latestRoundIndex = roundIndex;
        latestStatus = 'correct';
      }
    });
  });

  // check wrong and override if more recent
  wrongAnswers.forEach((round, roundIndex) => {
    round.forEach(c => {
      if (
        renderValue(c.left, gameType) === lopStr &&
        renderValue(c.right, gameType) === ropStr &&
        roundIndex >= latestRoundIndex
      ) {
        latestRoundIndex = roundIndex;
        latestStatus = 'wrong';
      }
    });
  });

  return latestStatus;
};

export const OperationChart: React.FC<TOperationChartProps> = ({
  gameType,
  width = 390,
  bottomPadding = 12,
  rightPadding = 1,
  wrongAnswers = [],
  correctAnswers = [],
}) => {
  if (gameType === 'All') {
    return <Text>Select a specific game type to view the operation chart.</Text>;
  }

  const config = GAME_TYPES_CONFIG[gameType];
  if (!config) {
    return <Text>Invalid game type</Text>;
  }

  const leftOperands: OPERAND_TYPE[] = (config as any).leftOperands || config.operands || [];
  const rightOperands: OPERAND_TYPE[] = (config as any).rightOperands || config.operands || [];

  const verticalCount = leftOperands.length + 2;
  const horizontalCount = rightOperands.length + 2;

  const innerWidth = width - rightPadding;
  const baseCellSize = Math.floor(innerWidth / horizontalCount);
  const remainder = innerWidth % horizontalCount;

  const chartHeight = verticalCount * baseCellSize;
  const totalHeight = chartHeight + bottomPadding;
  const fontSize = Math.max(10, Math.floor(baseCellSize * 0.32));

  const getColWidth = (idx: number) => baseCellSize + (idx < remainder ? 1 : 0);

  return (
    <View style={[styles.wrapper, { width, height: totalHeight }]}>
      <View style={{ flexDirection: 'row' }}>
        <View style={{ width: innerWidth, height: chartHeight, backgroundColor: '#FFF5DD' }}>
          {/* Top row */}
          <View style={{ flexDirection: 'row' }}>
            <View style={[styles.emptyCell, { width: getColWidth(0), height: baseCellSize }]} />
            <View style={[styles.emptyCell, { width: getColWidth(1), height: baseCellSize }]} />
            <View
              style={[
                styles.topLabel,
                {
                  width: rightOperands.reduce((w, _, idx) => w + getColWidth(idx + 2), 0),
                  height: baseCellSize,
                },
              ]}
            >
              <Text style={[styles.axisText, { fontSize: fontSize + 2 }]}>Right</Text>
            </View>
          </View>

          {/* Grid */}
          <View style={{ flexDirection: 'row' }}>
            {/* Left label */}
            <View
              style={[
                styles.leftLabel,
                { width: isMobile ? 22 : 20, height: baseCellSize * (leftOperands.length + 1) },
              ]}
            >
              <Text
                style={[
                  styles.axisText,
                  { transform: [{ rotate: '-90deg' }], fontSize: fontSize + 2 },
                ]}
                ellipsizeMode="clip"
                adjustsFontSizeToFit
                {...(Platform.OS !== 'web' ? { numberOfLines: 1 } : {})}
              >
                Left
              </Text>
            </View>

            <View>
              {/* Header row */}
              <View style={{ flexDirection: 'row' }}>
                <View style={[styles.emptyCell, { width: getColWidth(0), height: baseCellSize }]} />
                {rightOperands.map((op, idx) => {
                  const colWidth = getColWidth(idx + 1);
                  return (
                    <View
                      key={idx}
                      style={[styles.headerCell, { width: colWidth, height: baseCellSize }]}
                    >
                      <Text style={[styles.headerText, { fontSize }]}>
                        {renderValue(op, gameType)}
                      </Text>
                    </View>
                  );
                })}
              </View>

              {/* Body rows */}
              {leftOperands.map((lop, i) => (
                <View key={i} style={{ flexDirection: 'row' }}>
                  <View
                    style={[styles.headerCell, { width: getColWidth(0), height: baseCellSize }]}
                  >
                    <Text style={[styles.headerText, { fontSize }]}>
                      {renderValue(lop, gameType)}
                    </Text>
                  </View>
                  {rightOperands.map((rop, j) => {
                    const colWidth = getColWidth(j + 1);
                    const status = findMostRecentStatus(
                      lop,
                      rop,
                      gameType,
                      correctAnswers,
                      wrongAnswers,
                    );
                    const result = performOperation({
                      gameType,
                      leftOperand: lop,
                      rightOperand: rop,
                    });

                    let cellStyle: object[] = [
                      styles.cell,
                      { width: colWidth, height: baseCellSize },
                    ];
                    let textStyle = [styles.text, { fontSize }];

                    if (status === 'correct') {
                      cellStyle.push({ backgroundColor: '#34C759' });
                    } else if (status === 'wrong') {
                      cellStyle.push({ backgroundColor: '#FF3B30' });
                    } else {
                      textStyle.push({ textAlign: 'center', color: '#fff5dd' });
                    }

                    return (
                      <View key={j} style={cellStyle}>
                        <Text style={textStyle}>{renderValue(result, gameType)}</Text>
                      </View>
                    );
                  })}
                </View>
              ))}
            </View>
          </View>
        </View>

        <View style={{ width: rightPadding, height: chartHeight, backgroundColor: '#fff5dd' }} />
      </View>

      <View style={{ height: bottomPadding, width, backgroundColor: '#fff5dd' }} />
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    borderColor: '#FFD885',
    backgroundColor: '#fff5dd',
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
  },
  cell: {
    borderWidth: 1,
    borderColor: '#FFD885',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerCell: {
    borderWidth: 1,
    borderColor: '#FFD885',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFD885',
  },
  emptyCell: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff5dd',
  },
  topLabel: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff5dd',
  },
  leftLabel: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff5dd',
  },
  text: {
    textAlign: 'center',
    color: '#ffffff',
  },
  headerText: {
    textAlign: 'center',
    fontWeight: '400',
    color: '#333',
  },
  axisText: {
    textAlign: 'center',
    fontWeight: '400',
    color: '#333',
    fontSize: 10,
  },
});
